/**
 * Phase 46 (Plan 46-07) — TanStack hooks for global notification routes.
 *
 * Consumed by GlobalNotificationRoutesPage. Hook key shape
 * `['globalNotificationRoutes']` is mirrored by the mutation invalidators.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchGlobalNotificationRoutes,
  createGlobalNotificationRoute,
  updateGlobalNotificationRoute,
  deleteGlobalNotificationRoute,
} from '../services/api/notificationRoutes';
import type {
  CreateGlobalNotificationRouteInput,
  UpdateGlobalNotificationRouteInput,
} from '../services/api/notificationRoutes';

export function useGlobalNotificationRoutes() {
  return useQuery({
    queryKey: ['globalNotificationRoutes'],
    queryFn: () => fetchGlobalNotificationRoutes(),
  });
}

export function useCreateGlobalNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGlobalNotificationRouteInput) =>
      createGlobalNotificationRoute(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalNotificationRoutes'] });
    },
  });
}

export function useUpdateGlobalNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      routeId,
      input,
    }: {
      routeId: number;
      input: UpdateGlobalNotificationRouteInput;
    }) => updateGlobalNotificationRoute(routeId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalNotificationRoutes'] });
    },
  });
}

export function useDeleteGlobalNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (routeId: number) => deleteGlobalNotificationRoute(routeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalNotificationRoutes'] });
    },
  });
}
